/**
 * The Cloudflare Turnstile widget on the public sign-in forms, ported from
 * SynRank. It reports the token the API redeems; an empty string means there
 * is no valid token right now, so the form stays blocked. Bump `resetKey`
 * after a failed submit: a token is single-use.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widget: string) => void;
      remove: (widget: string) => void;
    };
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// Cloudflare's published always-passing test key; only the local dev build falls back to it.
const DEV_TEST_SITE_KEY = '1x00000000000000000000AA';
export const SITE_KEY: string =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || (import.meta.env.DEV ? DEV_TEST_SITE_KEY : '');

let scriptLoading: Promise<NonNullable<Window['turnstile']>> | null = null;

function loadTurnstile(): Promise<NonNullable<Window['turnstile']>> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptLoading ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not start'));
    script.onerror = () => {
      scriptLoading = null;
      reject(new Error('Turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptLoading;
}

export function HumanCheck({
  onToken,
  resetKey = 0,
}: {
  onToken: (token: string) => void;
  resetKey?: number;
}): ReactNode {
  const box = useRef<HTMLDivElement | null>(null);
  const widget = useRef<string | null>(null);
  const report = useRef(onToken);
  report.current = onToken;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) {
      report.current('');
      return undefined;
    }
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !box.current) return;
        widget.current = turnstile.render(box.current, {
          sitekey: SITE_KEY,
          size: 'flexible',
          callback: (token: string) => {
            // Turnstile retries on its own after a failure; a later pass clears the warning.
            setFailed(false);
            report.current(token);
          },
          'expired-callback': () => report.current(''),
          'error-callback': () => {
            report.current('');
            setFailed(true);
          },
        });
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
      report.current('');
    };
  }, []);

  useEffect(() => {
    if (!resetKey || !widget.current || !window.turnstile) return;
    window.turnstile.reset(widget.current);
    report.current('');
  }, [resetKey]);

  if (!SITE_KEY) {
    return (
      <p className="li-error" role="alert">
        Human verification is not configured for this build, so this form is disabled.
      </p>
    );
  }
  return (
    <div className="li-human">
      <div ref={box} />
      {failed && (
        <p className="li-error" role="alert">
          The human check did not pass. It retries by itself; if it keeps failing, allow
          challenges.cloudflare.com and reload the page.
        </p>
      )}
    </div>
  );
}
